import * as Sentry from '@sentry/react';
import { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { MembershipFilters, MembershipFiltersState, DEFAULT_FILTERS } from '@/components/MembershipFilters';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn, formatLocalDate } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { gymApi } from '@/lib/api';
import { Membership, Client, Status } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { ExcelToolsActions } from '@/components/ExcelToolsActions';
import { buildMembershipExportRows } from '@/lib/export-utils';
import {
  Plus,
  Search,
  Filter,
  Loader2,
  CreditCard,
  Calendar,
  QrCode,
  User,
  Settings,
  Pencil,
  XCircle,
  Building2,
  RefreshCcw,
  History,
  AlertTriangle,
  Clock,
  ArrowRight
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { NoGymSelected } from '@/components/NoGymSelected';

export default function MembershipsPage() {
  const { user, gym, hasRole, hasModule, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = hasRole(['super_admin']);
  const canManageExcel = hasRole(['gym_admin', 'gym_supervisor']);

  const [memberships, setMemberships] = useState<any[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [membershipPlans, setMembershipPlans] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filters, setFilters] = useState<MembershipFiltersState>({ ...DEFAULT_FILTERS });
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingMembership, setEditingMembership] = useState<Membership | null>(null);
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const [historyClientId, setHistoryClientId] = useState<string | null>(null);
  const [clientHistory, setClientHistory] = useState<any[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    type: 'Básico Mensual',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    price: 29.99,
    status: 'active' as Status,
    payment_status: 'pending' as 'paid' | 'pending' | 'overdue',
    sessions_remaining: null as number | null,
  });

  const [isRenewOpen, setIsRenewOpen] = useState(false);
  const [renewTarget, setRenewTarget] = useState<Membership | null>(null);
  const [renewForm, setRenewForm] = useState({
    type: '',
    start_date: '',
    end_date: '',
    price: 0,
    sessions_remaining: null as number | null,
    payment_method: 'cash' as 'cash' | 'card' | 'transfer',
    periods: 1,
  });
  const [renewValidation, setRenewValidation] = useState({
    remainingDays: 0,
    isEarlyRenewal: false,
    oldEndDate: '',
  });
  const [isRenewSubmitting, setIsRenewSubmitting] = useState(false);

  const fetchData = async () => {
    // Guard: Si no es super_admin y no hay gym cargado, no buscamos datos todavía
    // Esto evita el "flash" de planes globales en conexiones lentas
    if (!isSuperAdmin && !gym?.gym_id) {
      if (!isAuthLoading) setIsLoading(false);
      return;
    }

    try {
      const gId = gym?.gym_id || undefined;

      // Auto-sync memberships first if gym is selected
      if (gId) await gymApi.syncMemberships(gId);

      const [membershipsRes, clientsRes, plansRes] = await Promise.all([
        gymApi.listMemberships(null, gId),
        gymApi.listClients(null, gId),
        gymApi.listMembershipPlans(gId)
      ]);

      if (membershipsRes.success) setMemberships(membershipsRes.memberships);
      if (clientsRes.success) setClients(clientsRes.clients);
      if (plansRes.success) setMembershipPlans(plansRes.plans);
    } catch (error) {
      console.error('Failed to fetch memberships data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    Sentry.setTag("area", "payments");
  }, []);

  useEffect(() => {
    if (!isAuthLoading) {
      fetchData();
    }
  }, [gym?.gym_id, isAuthLoading]);

  // Handle search parameter from URL
  useEffect(() => {
    const search = searchParams.get('search');
    const clientId = searchParams.get('clientId');

    if (clientId) {
      setFilters(prev => ({ ...prev, search: clientId }));
    } else if (search) {
      setFilters(prev => ({ ...prev, search }));
    }
  }, [searchParams]);

  const getClientName = (clientId: string) => {
    const client = clients.find(c => c.client_id === clientId);
    return client?.name || 'Cliente no encontrado';
  };

  const activeFilterCount = useMemo(() => {
    const entries = [
      filters.search,
      filters.type !== 'all' ? filters.type : '',
      filters.status !== 'all' ? filters.status : '',
      filters.paidFrom,
      filters.paidTo,
    ];
    return entries.filter(Boolean).length;
  }, [filters]);

  const filteredMemberships = memberships
    .filter((m) => {
      const clientName = getClientName(m.client_id).toLowerCase();
      const search = filters.search.toLowerCase();
      const matchesSearch =
        !search ||
        clientName.includes(search) ||
        m.client_id.toLowerCase().includes(search) ||
        m.membership_id.toLowerCase().includes(search) ||
        m.type.toLowerCase().includes(search);
      const matchesType = filters.type === 'all' || m.type === filters.type;
      const matchesStatus = filters.status === 'all' || m.status === filters.status;
      const matchesPaidAt = (() => {
        if (!filters.paidFrom && !filters.paidTo) return true;
        if (!m.paid_at) return false;
        const tz = gym?.timezone || 'UTC';
        const paidDate = new Date(m.paid_at).toLocaleDateString('sv-SE', { timeZone: tz });
        if (filters.paidFrom && filters.paidTo) {
          return paidDate >= filters.paidFrom && paidDate <= filters.paidTo;
        }
        if (filters.paidFrom) {
          return paidDate === filters.paidFrom;
        }
        return paidDate <= filters.paidTo!;
      })();
      return matchesSearch && matchesType && matchesStatus && matchesPaidAt;
    })
    .sort((a, b) => {
      if (filters.sortBy === 'client_name') {
        const nameA = getClientName(a.client_id);
        const nameB = getClientName(b.client_id);
        return filters.sortOrder === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }
      const dateA = a.created_at || '';
      const dateB = b.created_at || '';
      return filters.sortOrder === 'asc'
        ? dateA.localeCompare(dateB)
        : dateB.localeCompare(dateA);
    });


  const getSummaryStats = () => {
    const today = new Date().toISOString().split('T')[0];
    const threeDaysFromNow = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const expiringToday = memberships.filter(m => m.status === 'active' && m.end_date === today).length;
    const expiringSoon = memberships.filter(m => m.status === 'active' && m.end_date > today && m.end_date <= threeDaysFromNow).length;
    const lowSessions = memberships.filter(m => m.status === 'active' && m.sessions_remaining !== null && m.sessions_remaining <= 2).length;

    return { expiringToday, expiringSoon, lowSessions };
  };

  const computeRenewPeriod = (plan: any, start: string, periods: number) => {
    const duration = plan?.duration_days || 30;
    const startDate = new Date(start + 'T00:00:00');
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + duration * periods);
    return {
      end_date: endDate.toISOString().split('T')[0],
      price: Math.round((Number(plan?.price) || 0) * periods * 100) / 100,
      sessions_remaining: plan?.total_sessions != null ? plan.total_sessions * periods : null,
    };
  };

  const handlePeriodsChange = (periods: number) => {
    const p = Math.max(1, Math.floor(periods) || 1);
    const plan = membershipPlans.find(x => x.name === renewForm.type);
    const { end_date, price, sessions_remaining } = computeRenewPeriod(plan, renewForm.start_date, p);
    setRenewForm(prev => ({ ...prev, periods: p, end_date, price, sessions_remaining }));
  };

  const handleQuickRenew = (membership: any) => {
    const oldEndDate = new Date(membership.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(Math.max(today.getTime(), oldEndDate.getTime() + 24 * 60 * 60 * 1000)).toISOString().split('T')[0];

    // Calculate duration based on previous membership
    const prevStart = new Date(membership.start_date);
    const diffTime = Math.abs(oldEndDate.getTime() - prevStart.getTime());
    const durationDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 30;

    const newEndDate = new Date(new Date(startDate).getTime() + durationDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    setFormData({
      client_id: membership.client_id,
      type: membership.type,
      start_date: startDate,
      end_date: newEndDate,
      price: membership.price,
      status: 'active',
      payment_status: 'paid', // Assuming renewal is paid
      sessions_remaining: membershipPlans.find(p => p.name === membership.type)?.total_sessions || null,
    });
    setIsCreateOpen(true);
  };

  const handleRenewClick = async (membership: Membership) => {
    const gId = gym?.gym_id;
    if (!gId) return;

    const requireCashBox = hasModule('sales');
    const boxRes = await gymApi.getActiveBox(gId);
    if (requireCashBox && (!boxRes.success || !boxRes.box)) {
      toast({
        title: 'Caja cerrada',
        description: 'Debe abrir una caja para renovar membresías.',
        variant: 'destructive',
      });
      return;
    }

    const plan = membershipPlans.find(p => p.name === membership.type);
    const duration = plan?.duration_days || 30;
    const oldEndDate = new Date(membership.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const remainingMs = oldEndDate.getTime() - today.getTime();
    const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));

    // If the membership already expired, the renewal starts today; otherwise it continues the day after the previous end date.
    const startDate = new Date(Math.max(today.getTime(), oldEndDate.getTime() + 24 * 60 * 60 * 1000));
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + duration);

    setRenewTarget(membership);
    setRenewForm({
      type: membership.type,
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0],
      price: membership.price,
      sessions_remaining: plan?.total_sessions || null,
      payment_method: 'cash',
      periods: 1,
    });
    setRenewValidation({
      remainingDays: remainingDays > 0 ? remainingDays : 0,
      isEarlyRenewal: false,
      oldEndDate: membership.end_date,
    });
    setIsRenewOpen(true);
  };

  const handleConfirmRenew = async () => {
    const gId = gym?.gym_id;
    if (!gId || !renewTarget || !user) return;

    if (renewValidation.remainingDays > 0) {
      const isAdminOrSuper = hasRole(['gym_admin', 'super_admin']);
      const newStart = new Date(renewForm.start_date + 'T00:00:00');
      const oldEnd = new Date(renewTarget.end_date + 'T00:00:00');
      if (newStart < oldEnd && !isAdminOrSuper) {
        toast({
          title: 'Renovación adelantada bloqueada',
          description: 'Solo administradores pueden solapar membresías. La fecha de inicio debe ser posterior al vencimiento actual.',
          variant: 'destructive',
        });
        return;
      }
    }

    setIsRenewSubmitting(true);
    try {
      const res = await gymApi.renewMembership({
        gym_id: gId,
        client_id: renewTarget.client_id,
        type: renewForm.type,
        start_date: renewForm.start_date,
        end_date: renewForm.end_date,
        price: renewForm.price,
        payment_method: renewForm.payment_method,
        user_id: user.id,
        sessions_remaining: renewForm.sessions_remaining,
        requireCashBox: hasModule('sales') && renewForm.payment_method === 'cash',
      }, gId);

      if (res.success) {
        if (renewForm.start_date > renewTarget.end_date) {
          try {
            await gymApi.updateMembership({
              membership_id: renewTarget.membership_id,
              client_id: renewTarget.client_id,
              type: renewTarget.type,
              start_date: renewTarget.start_date,
              end_date: renewTarget.end_date,
              price: renewTarget.price,
              status: 'expired',
              payment_status: renewTarget.payment_status,
              payment_method: renewTarget.payment_method,
              sessions_remaining: renewTarget.sessions_remaining,
            }, null, gId);
          } catch (e) {
            console.error('Error marking previous membership as expired:', e);
          }
        }

        await fetchData();
        setIsRenewOpen(false);
        setRenewTarget(null);
        toast({
          title: 'Membresía renovada',
          description: `La membresía de ${getClientName(renewTarget.client_id)} ha sido renovada exitosamente.`,
        });
      } else {
        throw new Error(res.error || 'Error al renovar membresía');
      }
    } catch (error: any) {
      console.error('Error renewing membership:', error);
      Sentry.captureException(error, {
        tags: { operation: 'renew_membership' },
        extra: { client_id: renewTarget.client_id, type: renewForm.type }
      });
      toast({
        title: 'Error al renovar',
        description: error?.message || 'No se pudo renovar la membresía',
        variant: 'destructive',
      });
    } finally {
      setIsRenewSubmitting(false);
    }
  };

  const fetchClientHistory = async (clientId: string) => {
    setIsHistoryLoading(true);
    setHistoryClientId(clientId);
    try {
      const res = await gymApi.getClientCheckIns(clientId);
      if (res.success) setClientHistory(res.check_ins);
    } catch (err) {
      console.error("Error fetching history:", err);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const stats = getSummaryStats();

  const handleCreateMembership = async () => {
    const gId = gym?.gym_id;
    if (!gId) {
      toast({
        title: 'Error',
        description: 'No se pudo determinar el gimnasio. Selecciona un gimnasio activo.',
        variant: 'destructive',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      // Automatically determine status based on end_date
      const today = new Date().toISOString().split('T')[0];
      const status = formData.end_date >= today ? 'active' : 'expired';

      const res = await gymApi.createMembership({
        ...formData,
        status
      }, null, gId);

      if (res.success) {
        await fetchData();
        setIsCreateOpen(false);
        resetForm();

        toast({
          title: 'Membresía creada',
          description: `La membresía ha sido registrada como ${status === 'active' ? 'Activa' : 'Vencida'}`,
        });
      }
    } catch (error: any) {
      console.error("Error creating membership:", error);
      Sentry.captureException(error, {
        tags: { operation: 'create_membership' },
        extra: { client_id: formData.client_id, type: formData.type, price: formData.price }
      });
      toast({
        title: 'Error al crear membresía',
        description: error?.message || 'No se pudo crear la membresía',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateMembership = async () => {
    if (!editingMembership) return;

    setIsSubmitting(true);
    try {
      // Automatically determine status based on end_date
      const today = new Date().toISOString().split('T')[0];
      const status = formData.end_date >= today ? 'active' : 'expired';

      const res = await gymApi.updateMembership({
        ...formData,
        membership_id: editingMembership.membership_id,
        status
      }, null, gym?.gym_id || undefined);

      if (res.success) {
        await fetchData();
        setEditingMembership(null);
        setIsCreateOpen(false);
        resetForm();
        toast({
          title: 'Membresía actualizada',
          description: `Los cambios han sido guardados. Estado: ${status === 'active' ? 'Activa' : 'Vencida'}`,
        });
      }
    } catch (error: any) {
      console.error("Error updating membership:", error);
      Sentry.captureException(error, {
        tags: { operation: 'update_membership' },
        extra: { membership_id: editingMembership.membership_id, client_id: formData.client_id }
      });
      toast({
        title: 'Error al actualizar membresía',
        description: error?.message || 'No se pudo actualizar la membresía',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMembership = async (membershipId: string) => {
    try {
      const res = await gymApi.deleteMembership(membershipId, null, gym?.gym_id || undefined);
      if (res.success) {
        await fetchData();
        toast({
          title: 'Membresía eliminada',
          description: 'El registro ha sido eliminado',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error al eliminar membresía',
        description: error?.message || 'No se pudo eliminar la membresía',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      type: 'Básico Mensual',
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      price: 29.99,
      status: 'active',
      payment_status: 'pending',
      sessions_remaining: null,
    });
  };

  const openEditDialog = (membership: Membership) => {
    setEditingMembership(membership);
    setFormData({
      client_id: membership.client_id,
      type: membership.type,
      start_date: membership.start_date,
      end_date: membership.end_date,
      price: membership.price,
      status: membership.status,
      payment_status: membership.payment_status,
      sessions_remaining: membership.sessions_remaining,
    });
    setIsCreateOpen(true);
  };

  const getStatusBadge = (status: Status) => {
    const variants: Record<string, 'success' | 'warning' | 'destructive' | 'muted'> = {
      active: 'success',
      pending: 'warning',
      inactive: 'muted',
      expired: 'destructive',
    };
    const labels: Record<string, string> = {
      active: 'Activa',
      pending: 'Pendiente',
      inactive: 'Inactiva',
      expired: 'Vencida',
    };

    const variant = variants[status as keyof typeof variants] || 'secondary';
    const label = labels[status as keyof typeof labels] || status;

    return <Badge variant={variant as any}>{label}</Badge>;
  };

  const getPaymentBadge = (status: 'paid' | 'pending' | 'overdue') => {
    const variants: Record<string, 'success' | 'warning' | 'destructive'> = {
      paid: 'success',
      pending: 'warning',
      overdue: 'destructive',
    };
    const labels: Record<string, string> = {
      paid: 'Pagado',
      pending: 'Pendiente',
      overdue: 'Vencido',
    };
    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Gestión de Membresías">
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Gestión de Membresías"
      subtitle={gym ? `Administra los planes y suscripciones de ${gym.name}` : "Administración global de membresías"}
      actions={
        <div className="flex items-center gap-2">
          {canManageExcel && (
            <ExcelToolsActions
              kind="memberships"
              exportRows={buildMembershipExportRows(filteredMemberships, clients)}
              exportFileName="membresias"
              importDisabled={!gym}
              onImported={fetchData}
            />
          )}
          <Button
            variant="outline"
            onClick={() => navigate('/settings?tab=membresias')}
            className="hidden lg:flex"
          >
            <Settings className="h-4 w-4 mr-2" />
            Configurar Planes
          </Button>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button variant="gradient" disabled={!gym} className="px-3 md:px-4">
                <Plus className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Nueva Membresía</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>
                  {editingMembership ? 'Editar Membresía' : 'Crear Nueva Membresía'}
                </DialogTitle>
                <DialogHeader>
                  <DialogDescription>
                    {editingMembership ? 'Modifica los detalles de la membresía.' : 'Asigna una membresía a un cliente'}
                  </DialogDescription>
                </DialogHeader>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <Select
                    value={formData.client_id}
                    onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.client_id} value={client.client_id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tipo de membresía</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => {
                      const plan = membershipPlans.find(p => p.name === value);
                      const duration = plan?.duration_days || 30;

                      // Calculate new end date based on current start date and new plan duration
                      const start = new Date(formData.start_date + 'T00:00:00');
                      const end = new Date(start);
                      end.setDate(start.getDate() + duration);
                      const endDateStr = end.toISOString().split('T')[0];

                      setFormData({
                        ...formData,
                        type: value,
                        price: plan?.price || 0,
                        sessions_remaining: plan?.total_sessions || null,
                        end_date: endDateStr
                      });
                    }}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {membershipPlans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.name}>
                          {plan.name} - ${Number(plan.price).toFixed(2)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fecha inicio</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => {
                        const newStartDate = e.target.value;
                        const plan = membershipPlans.find(p => p.name === formData.type);
                        const duration = plan?.duration_days || 30;

                        // Calculate new end date based on new start date
                        const start = new Date(newStartDate + 'T00:00:00');
                        const end = new Date(start);
                        end.setDate(start.getDate() + duration);
                        const endDateStr = end.toISOString().split('T')[0];

                        setFormData({
                          ...formData,
                          start_date: newStartDate,
                          end_date: endDateStr
                        });
                      }}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha fin</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Estado de pago</Label>
                  <Select
                    value={formData.payment_status}
                    onValueChange={(value: 'paid' | 'pending' | 'overdue') =>
                      setFormData({ ...formData, payment_status: value })
                    }
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Pagado</SelectItem>
                      <SelectItem value="pending">Pendiente</SelectItem>
                      <SelectItem value="overdue">Vencido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.sessions_remaining !== null && (
                  <div className="space-y-2">
                    <Label>Sesiones Restantes</Label>
                    <Input
                      type="number"
                      value={formData.sessions_remaining || 0}
                      onChange={(e) => setFormData({ ...formData, sessions_remaining: parseInt(e.target.value) })}
                      disabled={isSubmitting}
                    />
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setIsCreateOpen(false); setEditingMembership(null); resetForm(); }} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button
                  variant="gradient"
                  onClick={editingMembership ? handleUpdateMembership : handleCreateMembership}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {editingMembership ? 'Guardando...' : 'Creando...'}
                    </>
                  ) : (
                    editingMembership ? 'Guardar Cambios' : 'Crear Membresía'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={isRenewOpen} onOpenChange={(open) => { if (!open) { setIsRenewOpen(false); setRenewTarget(null); } }}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Renovar Membresía</DialogTitle>
                <DialogHeader>
                  <DialogDescription>
                    {renewTarget ? `Renovación para ${getClientName(renewTarget.client_id)}` : 'Configura la renovación de la membresía.'}
                  </DialogDescription>
                </DialogHeader>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <p className="text-sm font-medium text-foreground">
                    {renewTarget ? getClientName(renewTarget.client_id) : ''}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Plan</Label>
                  <Select
                    value={renewForm.type}
                    onValueChange={(value) => {
                      const plan = membershipPlans.find(p => p.name === value);
                      const { end_date, price, sessions_remaining } = computeRenewPeriod(plan, renewForm.start_date, renewForm.periods);
                      setRenewForm({
                        ...renewForm,
                        type: value,
                        price,
                        sessions_remaining,
                        end_date,
                      });
                    }}
                    disabled={isRenewSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {membershipPlans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.name}>
                          {plan.name} - ${Number(plan.price).toFixed(2)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Cantidad de periodos</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    {[1, 3, 6, 12].map((n) => (
                      <Button
                        key={n}
                        type="button"
                        size="sm"
                        variant={renewForm.periods === n ? 'gradient' : 'outline'}
                        onClick={() => handlePeriodsChange(n)}
                        disabled={isRenewSubmitting}
                      >
                        {n}
                      </Button>
                    ))}
                    <Input
                      type="number"
                      min={1}
                      className="w-20"
                      value={renewForm.periods}
                      onChange={(e) => handlePeriodsChange(parseInt(e.target.value) || 1)}
                      disabled={isRenewSubmitting}
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Multiplica la duración y el precio por la cantidad de periodos.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Fecha inicio</Label>
                    <Input
                      type="date"
                      value={renewForm.start_date}
                        onChange={(e) => {
                          const newStart = e.target.value;
                          const plan = membershipPlans.find(p => p.name === renewForm.type);
                          const { end_date, price, sessions_remaining } = computeRenewPeriod(plan, newStart, renewForm.periods);
                          const oldEnd = renewTarget ? new Date(renewTarget.end_date + 'T00:00:00') : null;
                          const start = new Date(newStart + 'T00:00:00');
                          const isEarly = oldEnd ? start < oldEnd : false;
                          setRenewForm({ ...renewForm, start_date: newStart, end_date, price, sessions_remaining });
                          setRenewValidation(prev => ({ ...prev, isEarlyRenewal: isEarly }));
                        }}
                      disabled={isRenewSubmitting}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha fin</Label>
                    <Input
                      type="date"
                      value={renewForm.end_date}
                      onChange={(e) => setRenewForm({ ...renewForm, end_date: e.target.value })}
                      disabled={isRenewSubmitting}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Precio</Label>
                  <p className="text-lg font-bold text-foreground">${renewForm.price.toFixed(2)}</p>
                  {renewForm.periods > 1 && (
                    <p className="text-xs text-muted-foreground">
                      {renewForm.periods} × ${(renewForm.price / renewForm.periods).toFixed(2)}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Método de pago</Label>
                  <Select
                    value={renewForm.payment_method}
                    onValueChange={(value: 'cash' | 'card' | 'transfer') =>
                      setRenewForm({ ...renewForm, payment_method: value })
                    }
                    disabled={isRenewSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Efectivo</SelectItem>
                      <SelectItem value="card">Tarjeta</SelectItem>
                      <SelectItem value="transfer">Transferencia</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {renewForm.sessions_remaining !== null && (
                  <div className="space-y-2">
                    <Label>Sesiones Restantes</Label>
                    <Input
                      type="number"
                      value={renewForm.sessions_remaining || 0}
                      onChange={(e) => setRenewForm({ ...renewForm, sessions_remaining: parseInt(e.target.value) })}
                      disabled={isRenewSubmitting}
                    />
                  </div>
                )}
                {renewValidation.remainingDays > 0 && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                    <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-yellow-600 dark:text-yellow-400">
                      La membresía actual vence el{' '}
                      <strong>{formatLocalDate(renewValidation.oldEndDate)}</strong>{' '}
                      ({renewValidation.remainingDays} días restantes). La nueva membresía iniciará el{' '}
                      <strong>{formatLocalDate(renewForm.start_date)}</strong>.
                    </p>
                  </div>
                )}
                {renewValidation.isEarlyRenewal && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                    <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-red-600 dark:text-red-400">
                      La fecha de inicio es anterior al vencimiento actual. Solo administradores pueden realizar renovaciones adelantadas con solapamiento.
                    </p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => { setIsRenewOpen(false); setRenewTarget(null); }}
                  disabled={isRenewSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  variant="gradient"
                  onClick={handleConfirmRenew}
                  disabled={isRenewSubmitting}
                >
                  {isRenewSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Renovando...
                    </>
                  ) : (
                    'Confirmar Renovación'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      }
    >
      {isSuperAdmin && !gym && <NoGymSelected />}

      {/* Summary Stats / Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card
          className={cn("cursor-pointer transition-all hover:scale-[1.02] border-l-4", stats.expiringToday > 0 ? "border-l-destructive" : "border-l-muted")}
          onClick={() => setFilters(prev => ({ ...prev, status: stats.expiringToday > 0 ? 'active' : 'all' }))}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase">Vencen Hoy</p>
                <h3 className="text-2xl font-bold mt-1">{stats.expiringToday}</h3>
              </div>
              <div className={cn("p-3 rounded-xl", stats.expiringToday > 0 ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground")}>
                <AlertTriangle className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={cn("cursor-pointer transition-all hover:scale-[1.02] border-l-4", stats.expiringSoon > 0 ? "border-l-orange-500" : "border-l-muted")}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase">Vencen pronto (3d)</p>
                <h3 className="text-2xl font-bold mt-1">{stats.expiringSoon}</h3>
              </div>
              <div className={cn("p-3 rounded-xl", stats.expiringSoon > 0 ? "bg-orange-500/10 text-orange-500" : "bg-muted text-muted-foreground")}>
                <Clock className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={cn("cursor-pointer transition-all hover:scale-[1.02] border-l-4", stats.lowSessions > 0 ? "border-l-primary" : "border-l-muted")}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase">Bajas Sesiones (≤2)</p>
                <h3 className="text-2xl font-bold mt-1">{stats.lowSessions}</h3>
              </div>
              <div className={cn("p-3 rounded-xl", stats.lowSessions > 0 ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground")}>
                <ArrowRight className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <MembershipFilters
            filters={filters}
            onChange={setFilters}
            plans={membershipPlans}
            activeFilterCount={activeFilterCount}
          />
        </CardContent>
      </Card>

      {/* Memberships Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            Membresías ({filteredMemberships.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 lg:p-6">
          {/* Desktop Table View */}
          <div className="hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  {isSuperAdmin && !gym && <TableHead>Gimnasio</TableHead>}
                  <TableHead>Tipo</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead>Precio</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Sesiones</TableHead>
                  <TableHead>Pago</TableHead>
                  <TableHead className="text-right w-[150px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMemberships.map((membership) => (
                  <TableRow key={membership.membership_id}>
                    <TableCell>
                      <button
                        onClick={() => fetchClientHistory(membership.client_id)}
                        className="text-left hover:text-primary transition-colors group"
                      >
                        <p className="font-medium flex items-center gap-1">
                          {getClientName(membership.client_id)}
                          <History className="h-3 w-3 opacity-0 group-hover:opacity-50" />
                        </p>
                        <p className="text-sm text-muted-foreground">{membership.membership_id}</p>
                      </button>
                    </TableCell>
                    {isSuperAdmin && !gym && (
                      <TableCell>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Building2 className="h-3 w-3" />
                          <span className="text-xs font-medium uppercase tracking-wider">
                            {membership.gym_id?.substring(0, 8)}...
                          </span>
                        </div>
                      </TableCell>
                    )}
                    <TableCell>{membership.type}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{formatLocalDate(membership.start_date)}</p>
                        <p className="text-muted-foreground">
                          al {formatLocalDate(membership.end_date)}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">${Number(membership.price || 0).toFixed(2)}</span>
                    </TableCell>
                    <TableCell>{getStatusBadge(membership.status)}</TableCell>
                    <TableCell>
                      {membership.sessions_remaining !== null ? (
                        <Badge variant="outline" className="font-mono">
                          {membership.sessions_remaining} restantes
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs italic">Ilimitadas</span>
                      )}
                    </TableCell>
                    <TableCell>{getPaymentBadge(membership.payment_status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-primary border-primary/30 hover:bg-primary/10"
                          onClick={() => handleRenewClick(membership)}
                        >
                          <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />
                          Renovar
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(membership)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (window.confirm(`¿Eliminar la membresía de ${getClientName(membership.client_id)}? Esta acción no se puede deshacer.`)) {
                              handleDeleteMembership(membership.membership_id);
                            }
                          }}
                          className="text-destructive"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="block lg:hidden divide-y divide-border">
            {filteredMemberships.map((membership) => (
              <div key={membership.membership_id} className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-foreground">{getClientName(membership.client_id)}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{membership.type}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEditDialog(membership)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => {
                        if (window.confirm(`¿Eliminar la membresía de ${getClientName(membership.client_id)}?`)) {
                          handleDeleteMembership(membership.membership_id);
                        }
                      }}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-[10px] uppercase text-muted-foreground font-medium">Período</p>
                    <div className="text-sm">
                      <p>{formatLocalDate(membership.start_date)}</p>
                      <p className="text-muted-foreground">al {formatLocalDate(membership.end_date)}</p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] uppercase text-muted-foreground font-medium">Precio</p>
                    <p className="text-sm font-bold text-foreground">${Number(membership.price || 0).toFixed(2)}</p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-primary border-primary/30 hover:bg-primary/10"
                  onClick={() => handleRenewClick(membership)}
                >
                  <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />
                  Renovar Membresía
                </Button>

                <div className="flex items-center gap-2 pt-1">
                  {getStatusBadge(membership.status)}
                  {membership.sessions_remaining !== null && (
                    <Badge variant="outline" className="font-mono text-[10px]">
                      {membership.sessions_remaining} ses.
                    </Badge>
                  )}
                  {getPaymentBadge(membership.payment_status)}
                </div>
              </div>
            ))}
          </div>

          {filteredMemberships.length === 0 && (
            <div className="h-24 flex items-center justify-center text-muted-foreground">
              No se encontraron membresías.
            </div>
          )}
        </CardContent>
      </Card>
      {/* History Sheet */}
      <Sheet open={!!historyClientId} onOpenChange={(open) => !open && setHistoryClientId(null)}>
        <SheetContent side="right" className="block sm:max-w-md bg-card border-l border-border">
          <SheetHeader className="pb-6 border-b">
            <SheetTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              Historial de Check-ins
            </SheetTitle>
            <SheetDescription className="text-base font-medium text-foreground">
              {historyClientId && getClientName(historyClientId)}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-8 space-y-4">
            {isHistoryLoading ? (
              <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : clientHistory.length > 0 ? (
              clientHistory.map((h, i) => (
                <div key={i} className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border transition-colors hover:bg-muted">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "h-10 w-10 rounded-full flex items-center justify-center",
                      h.result === 'allowed' ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"
                    )}>
                      <Clock className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{h.message}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(h.timestamp).toLocaleString('es-ES', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] uppercase font-bold px-2">
                    {h.method === 'admin_scanner' ? 'Admin' : 'Socio'}
                  </Badge>
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p>No hay registros recientes</p>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </DashboardLayout >
  );
}
